import React, {useState, useRef, useContext, useEffect} from "react"
import {useParams} from "react-router-dom"
import {DataTable} from "primereact/datatable"
import {Column} from "primereact/column"

import AddUnitModal from "../../components/AddUnitModal"
import CenterLoader from "../../components/CenterLoader"
import {Main, FiltersTable} from "../../components/Layout"
import SubHeader from "../../components/SubHeader"
import UploadButton from "../../components/UploadButton"

import {bulkFilter} from "../../utilities/general"
import RefContext from "../../utilities/RefContext"
import {wrappedGet, wrappedDelete, wrappedPut} from "../../utilities/AxiosHelper"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

import {getCellCustom, getExportableField} from "../../utilities/admin"

/**
 * Facility Component
 * Renders the Facility view
 */
const Facility = () => {
    const {organizationId, facilityId} = useParams()
    const navigate = useStableNavigate()
    const tableRef = useRef(null)
    const {dispatch} = useContext(RefContext)

    const [facility, setFacility] = useState({name: "", shortname: "", active: true, customerKey: ""})
    const [orgName, setOrgName] = useState("")
    const [searchText, setSearchText] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [units, setUnits] = useState([])
    const [filteredUnits, setFilteredUnits] = useState([])
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        if (facility.name !== "") {
            document.title = `Facility - ${facility.name}`
        }
    }, [facility])

    useEffect(() => {
        setIsLoading(true)
        Promise.all([fetchOrgData(), fetchFacilityData(), fetchFacilityUnitData()])
            .catch(() => {
                // optional: global error handling if you want
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    const fetchOrgData = () => {
        return wrappedGet(`organization/${organizationId}`)
            .then((response) => {
                const data = response.data
                if (!data) return

                if (data.name !== undefined) setOrgName(data.name)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Organization",
                    timeout: 5000,
                })
            })
    }

    const fetchFacilityData = () => {
        return wrappedGet(`organization/${organizationId}/facility/${facilityId}`)
            .then((response) => {
                const data = response.data

                if (!data) return

                let customerKey = ""
                if (data.customer_key !== undefined && data.customer_key !== null) {
                    customerKey = data.customer_key
                }

                setFacility({name: data.name, shortname: data.shortname, active: data.active, customerKey})
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Facility",
                    timeout: 5000,
                })
            })
    }

    const fetchFacilityUnitData = () => {
        return wrappedGet(`organization/${organizationId}/facility/${facilityId}/unit`)
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setUnits(response.data.results)
                    setFilteredUnits(response.data.results)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Facility Units",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const editUnit = (selectedRow) => {
        navigate(`/admin/organization/${organizationId}/facility/${facilityId}/unit/${selectedRow.id}`)
    }

    const handleReset = () => {
        setSearchText("")
    }

    const handleSave = () => {
        setIsLoading(true)
        wrappedPut(`organization/${organizationId}/facility/${facilityId}`, facility)
            .then(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "Facility Updated",
                    timeout: 5000,
                })
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "error",
                    text: "Problem fetching Facility Units",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const deleteUnit = (unit) => {
        let confirmAction = window.confirm("Are you sure you want to disable this unit?")
        if (confirmAction) {
            wrappedDelete(`organization/${organizationId}/facility/${facilityId}/unit/${unit.id}`)
                .then((response) => {
                    if (response.status === 200) {
                        dispatch("showToast", {
                            show: true,
                            variant: "success",
                            text: "Unit is disabled",
                            timeout: 5000,
                        })
                        fetchFacilityUnitData()
                    } else {
                        dispatch("showToast", {
                            show: true,
                            variant: "danger",
                            text: "Error disabling Unit",
                            timeout: 5000,
                        })
                        return
                    }
                })
                .catch(() => {
                    dispatch("showToast", {
                        show: true,
                        variant: "danger",
                        text: "Error disabling Unit",
                        timeout: 5000,
                    })
                    return
                })
        }
    }

    const columns = [
        {header: "Name", field: "name", export: true},
        {header: "Short Name", field: "shortname", export: true},
        {header: "Shift Length", field: "shift_length_hours", export: true},
        {header: "Customer Key", field: "customer_key", export: true},
        {header: "Active", field: "active", export: true},
        {header: "Created At", field: "created_at", export: true},
        {header: "Updated At", field: "updated_at", export: true},
        {header: "Actions", field: "actions", export: false},
    ]

    const addUnit = () => {
        setShowModal(true)
    }

    const hideModal = () => {
        setShowModal(false)
        setIsLoading(true)
        fetchFacilityUnitData()
    }

    const handleBack = () => {
        navigate(`/admin/organization/${organizationId}`)
    }

    if (isLoading) {
        return <CenterLoader />
    }

    const helpContent = (
        <>
            <h4>Instructions</h4>
            <p>Bulk uploading Units requires a CSV file with the following fields</p>

            <ol>
                <li>
                    <strong>customer_key:</strong> a unique identifier used by your organization. This will be used if
                    you would like to update this unit. This field must not exceed 255 characters
                </li>
                <li>
                    <strong>name:</strong> The name of your unit
                </li>
                <li>
                    <strong>shortname:</strong> any shortname for your unit
                </li>
                <li>
                    <strong>active:</strong> not required but can be specified as a boolean as either 'TRUE' or 'FALSE'
                </li>
                <li>
                    <strong>level_of_care:</strong> this can be a value of "Acute Care", "Progressive Care", "Procedural
                    Care", "Critical Care", "Ambulatory Care",
                </li>
                <li>
                    <strong>shift_length_hours:</strong> the number of hours in a shift for this unit
                </li>
                <li>
                    <strong>num_onboarding_weeks:</strong> The number of weeks for onboarding prior to starting
                    orientation.
                </li>
                <li>
                    <strong>precision_staffing:</strong> To Be Defined
                </li>
                <li>
                    <strong>ael_new_graduate:</strong> Average Orientation Length for New Graduate. This can be defined
                    per user type delimited by "|". Example: `RN:12|LPN:12` would set 12 weeks for an RN and LPN
                </li>
                <li>
                    <strong>ael_with_specialty_experience:</strong> Average Orientation Length for Employee with
                    Specialty Experience. This can be defined per user type delimited by "|"
                </li>
                <li>
                    <strong>ael_new_to_specialty:</strong> Average Orientation Length for Employee new to Specialty.
                    This can be defined per user type delimited by "|"
                </li>
            </ol>
            <h5>Example CSV File</h5>
            <pre>
                customer_key,name,shortname,active,level_of_care,shift_length_hours,shifts_per_week,
                num_onboarding_weeks,num_onboarding_weeks,ael_new_graduate,ael_with_specialty_experience,ael_new_to_specialty
                <br />
                u-1,Floor 1,Flr1,TRUE,Procedural Care,12,3,1,4,1,RN:12|LPN:12,RN:8|LPN:8,RN:10|LPN:10
                <br />
                u-2,Floor 2,Flr2,TRUE,Procedural Care,12,3,2,4,1,RN:2|LPN:2,RN:4|LPN:4,RN:6|LPN:6
            </pre>
        </>
    )

    return (
        <>
            <SubHeader title1={`Org: ${orgName}`} title2={`Facility: ${facility.name}`} icon="fa-hospital">
                <div className="d-flex flex-grow-1 justify-content-end">
                    <div className="d-flex flex-grow-1 justify-content-end">
                        <button className="btn btn-secondary fw-semibold me-2 px-6" onClick={handleBack}>
                            <i className="fas fa-arrow-left"></i>Back
                        </button>
                        <button className="btn btn-primary fw-semibold me-2 px-6" onClick={addUnit}>
                            <i className="fas fa-plus"></i>Add Unit
                        </button>
                        <UploadButton
                            uploadUrl={`bulk/upload/${organizationId}/${facilityId}/units`}
                            label="Bulk Load Units"
                            dispatch={dispatch}
                            reload={fetchFacilityUnitData}
                            helpTitle="Bulk Upload Units"
                            helpContent={helpContent}
                        />
                    </div>
                </div>
            </SubHeader>
            <Main>
                <div className="content d-flex flex-column flex-column-fluid">
                    <div className="container-fluid">
                        <div className="py-3 py-lg-6">
                            <div className="card">
                                <div className="col-xxl-12">
                                    <div className="row g-8 p-8 d-flex flex-row align-items-end">
                                        <div className="col">
                                            <div className="row g-8 align-items-end">
                                                <div className="col-4">
                                                    <label
                                                        htmlFor="id-facility-name"
                                                        className="form-label fw-bold required"
                                                    >
                                                        Name:
                                                    </label>
                                                    <input
                                                        id="id-facility-name"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12 required"
                                                        placeholder="Name"
                                                        value={facility.name}
                                                        onChange={(evt) => {
                                                            setFacility((prev) => ({
                                                                ...prev,
                                                                name: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label
                                                        htmlFor="id-facility-shortname"
                                                        className="form-label fw-bold required"
                                                    >
                                                        Short Name:
                                                    </label>
                                                    <input
                                                        id="id-facility-shortname"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12 required"
                                                        placeholder="Short Name"
                                                        value={facility.shortname}
                                                        onChange={(evt) => {
                                                            setFacility((prev) => ({
                                                                ...prev,
                                                                shortname: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label
                                                        htmlFor="id-facility-customer-key"
                                                        className="form-label fw-bold"
                                                    >
                                                        Customer Key:
                                                    </label>
                                                    <input
                                                        id="id-facility-customer-key"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Key"
                                                        value={facility.customerKey}
                                                        onChange={(evt) => {
                                                            setFacility((prev) => ({
                                                                ...prev,
                                                                customerKey: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label htmlFor="id-facility-active" className="form-label fw-bold">
                                                        Active:
                                                    </label>
                                                    <select
                                                        className="form-select form-select-solid fw-bold"
                                                        id="id-facility-active"
                                                        value={facility.active ? "true" : "false"}
                                                        onChange={(evt) => {
                                                            setFacility((prev) => ({
                                                                ...prev,
                                                                active: evt.target.value === "true",
                                                            }))
                                                        }}
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                </div>

                                                <div className="col-lg-2 col-md-6">
                                                    <div className="d-flex justify-content-start">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-secondary fw-semibold me-2 px-6"
                                                            onClick={handleSave}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <FiltersTable
                    Filters={
                        <div className="row g-8 align-items-end">
                            <div className="col-12">
                                <h3>Units</h3>
                            </div>
                            <div className="col-lg-2 col-md-12">
                                <label htmlFor="id-search-users" className="form-label fw-bold">
                                    Search Units:
                                </label>
                                <div className="d-flex align-items-center position-relative">
                                    <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                                    <input
                                        id="id-search-users"
                                        type="text"
                                        className="form-control form-control-solid ps-12"
                                        placeholder="Name"
                                        value={searchText}
                                        onChange={(evt) => {
                                            setSearchText(evt.target.value)
                                            const filteredUnits = bulkFilter(units, [
                                                [["name"], evt.target.value, "inexact"],
                                            ])
                                            setFilteredUnits(filteredUnits)
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6">
                                <div className="d-flex justify-content-start">
                                    <button
                                        type="reset"
                                        className="btn btn-secondary fw-semibold me-2 px-6"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                    Table={
                        <DataTable
                            ref={tableRef}
                            value={filteredUnits}
                            tableClassName="table gy-3 gs-5 rounded w-100 mt-3 align-middle"
                            paginator
                            rows={25}
                            sortField={columns[0].field}
                            sortOrder={1}
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            rowHover={true}
                            onMouseDownCapture={(e) => {
                                e.stopPropagation()
                            }}
                        >
                            {columns.map((colDef, index) => (
                                <Column
                                    key={index}
                                    field={colDef.field}
                                    header={colDef.header}
                                    headerClassName="th-sorting-center align-middle text-start text-light text-uppercase bg-gray-700 fs-7 fw-bold p-sortable-column"
                                    sortable
                                    body={(row) => getCellCustom(colDef, row, editUnit, deleteUnit, false)}
                                    exportable={colDef.export}
                                    exportField={(row) => getExportableField(colDef, row)}
                                />
                            ))}
                        </DataTable>
                    }
                />
            </Main>
            <AddUnitModal
                showModal={showModal}
                closeModal={hideModal}
                organizationId={organizationId}
                facilityId={facilityId}
            />
        </>
    )
}

export default Facility
